import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import { getEnvVar } from "@/common/utils/environment";
import { contentTypeFor } from "@/common/utils/upload-content-type";

/**
 * Storage's resumable endpoint for uploads authorized by a server-issued
 * token instead of the user's JWT (docs/storage-direct-access-migration.md).
 */
const SIGNED_UPLOAD_URL = `${getEnvVar("VITE_PUBLIC_SUPABASE_URL")}/storage/v1/upload/resumable/sign`;

const TUS_CHUNK_SIZE_BYTES = 1024 * 1024;

/**
 * Upload a single file to Supabase Storage via TUS (resumable uploads).
 * The API reserves the path and returns `uploadToken` for exactly that path;
 * Storage checks the token, so no user JWT is sent.
 *
 * The object is stored under `contentTypeFor(file)`, the type
 * `declareUploadFile` reserves: Storage keeps the TUS `contentType` metadata as
 * the object's type, and the server's scan quarantines an object whose type
 * differs from its reservation, so a HEIC (browser type "") must not be stored
 * under a type nobody reserved.
 */
export const tusUploadFile = async ({
  file,
  bucketName,
  objectName,
  uploadToken,
}: {
  file: File;
  bucketName: string;
  objectName: string;
  uploadToken: string;
}): Promise<void> => {
  const contentType = contentTypeFor(file);
  const uppy = new Uppy({
    restrictions: { maxNumberOfFiles: 1 },
    autoProceed: true,
  }).use(Tus, {
    endpoint: SIGNED_UPLOAD_URL,
    // The anon key only identifies the project; the signature authorizes
    // the upload. An Authorization header would make Storage use the JWT.
    headers: {
      apikey: getEnvVar("VITE_PUBLIC_SUPABASE_ANON_KEY"),
      "x-signature": uploadToken,
    },
    storeFingerprintForResuming: false,
    removeFingerprintOnSuccess: true,
    chunkSize: TUS_CHUNK_SIZE_BYTES,
    allowedMetaFields: [
      "bucketName",
      "objectName",
      "contentType",
      "cacheControl",
    ],
  });

  return new Promise<void>((resolve, reject) => {
    // Note: we intentionally don't call uppy.destroy() here. destroy()
    // triggers TUS `abort(true)` which sends a DELETE to Supabase — the
    // upload is already committed, so Supabase returns 409 and TUS retries
    // the DELETE several times before throwing an unhandled rejection.
    // Letting Uppy get garbage-collected avoids the 409 spam entirely.
    uppy.on("upload-success", () => {
      resolve();
    });

    uppy.on("upload-error", (_file, error) => {
      reject(error);
    });

    uppy.on("error", (error) => {
      reject(error);
    });

    uppy.addFile({
      name: file.name,
      type: contentType,
      data: file,
      meta: {
        bucketName,
        objectName,
        contentType,
      },
    });
  });
};
